import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Camera, Plus, Trash, MapPin, PencilSimple, PaintBrush, Images, FolderSimplePlus, Rows, ListDashes } from "@phosphor-icons/react";
import { AnnotationEditor } from "../components/AnnotationEditor";
import { CategoryField } from "../components/CategoryField";
import { Lightbox } from "../components/Lightbox";
import { TimemarkCamera } from "../components/TimemarkCamera";

const PHOTO_SPECIALTIES = ["Obra Civil", "Instalaciones Eléctricas", "Instalaciones Hidráulicas", "Acabados"];
const SECTION = "fotos";

export default function Photos() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [contractId, setContractId] = useState("");
  const [file, setFile] = useState(null);
  const [coords, setCoords] = useState(null);
  const [fSpec, setFSpec] = useState("");
  const [fContract, setFContract] = useState("");
  const [photoDragId, setPhotoDragId] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ caption: "", specialty: "", contract_id: "" });
  const [annotateFor, setAnnotateFor] = useState(null);
  const [annBusy, setAnnBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkSpecialty, setBulkSpecialty] = useState("");
  const [bulkContract, setBulkContract] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [foldersLayout, setFoldersLayout] = useState("h");
  const [lightbox, setLightbox] = useState(null);
  const bulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFiles.length) return toast.error("Selecciona una o varias imágenes");
    setBulkBusy(true);
    const fd = new FormData();
    bulkFiles.forEach((f) => fd.append("files", f));
    fd.append("specialty", bulkSpecialty);
    if (bulkContract) fd.append("contract_id", bulkContract);
    try {
      const r = await api.post("/photos/bulk", fd);
      toast.success(`${r.data.count} foto(s) subida(s). Arrástralas a una carpeta para clasificarlas.`);
      setBulkOpen(false); setBulkFiles([]); setBulkSpecialty(""); setBulkContract(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const addFolder = () => {
    const name = window.prompt("Nombre de la carpeta (especialidad, tarea, proveedor, etc.):");
    if (name && name.trim()) addCategory(name.trim());
  };
  const openEdit = (p) => { setEditForm({ caption: p.caption || "", specialty: p.specialty || "", contract_id: p.contract_id || "" }); setEditFor(p); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.put(`/photos/${editFor.id}`, editForm); toast.success("Foto actualizada"); setEditFor(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveAnnotation = async (file) => {
    setAnnBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", `(anotada) ${annotateFor.caption || ""}`.trim());
    fd.append("specialty", annotateFor.specialty || "");
    if (annotateFor.contract_id) {
      fd.append("contract_id", annotateFor.contract_id);
      const c = contracts.find((x) => x.id === annotateFor.contract_id);
      if (c) fd.append("contractor_id", c.contractor_id);
    }
    try { await api.post("/photos", fd); toast.success("Foto anotada guardada"); setAnnotateFor(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAnnBusy(false); }
  };
  const movePhoto = async (id, name) => { try { await api.put(`/photos/${id}/move`, { name }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const getLoc = () => {
    if (!navigator.geolocation) return toast.error("GPS no disponible");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }); toast.success("Ubicación adjuntada"); },
      () => toast.error("No se pudo obtener la ubicación"));
  };

  const load = () => api.get("/photos").then((r) => setItems(r.data));
  const loadCategories = () => api.get(`/doc-categories?section=${SECTION}`).then((r) => setCategories(r.data.map((c) => c.name))).catch(() => {});
  const addCategory = async (name) => { try { await api.post("/doc-categories", { section: SECTION, name }); loadCategories(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const deleteCategory = async (name) => { try { await api.delete(`/doc-categories?section=${SECTION}&name=${encodeURIComponent(name)}`); toast.success("Clasificación borrada de la lista"); loadCategories(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const uploadCaptured = async (file) => { const fd = new FormData(); fd.append("specialty", ""); fd.append("files", file); toast.loading("Subiendo foto…", { id: "tm" }); try { await api.post("/photos/bulk", fd); toast.success("Foto subida a la bandeja general. Clasifícala arrastrándola a una carpeta.", { id: "tm" }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail), { id: "tm" }); } };
  useEffect(() => { load(); loadCategories(); api.get("/contracts").then((r) => setContracts(r.data)); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecciona una imagen");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("caption", caption); fd.append("specialty", specialty);
    if (contractId) {
      fd.append("contract_id", contractId);
      const c = contracts.find((x) => x.id === contractId);
      if (c) fd.append("contractor_id", c.contractor_id);
    }
    if (coords) { fd.append("lat", coords.lat); fd.append("lng", coords.lng); }
    try {
      await api.post("/photos", fd);
      toast.success("Foto subida"); setOpen(false); setFile(null); setCaption(""); setSpecialty(""); setContractId(""); setCoords(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar foto?")) return; await api.delete(`/photos/${id}`); load(); };
  const cmap = Object.fromEntries(contracts.map((c) => [c.id, c]));

  const specialties = [...new Set(items.map((p) => p.specialty).filter(Boolean))];
  const allFolders = [...new Set([...specialties, ...categories])];
  const filtered = items.filter((p) => (!fSpec || p.specialty === fSpec) && (!fContract || p.contract_id === fContract));
  const groups = {};
  filtered.forEach((p) => { const k = p.specialty || "General / Sin especialidad"; (groups[k] = groups[k] || []).push(p); });
  const orderedKeys = [...PHOTO_SPECIALTIES.filter((s) => groups[s]), ...Object.keys(groups).filter((k) => !PHOTO_SPECIALTIES.includes(k))];

  const [dropActive, setDropActive] = useState(false);
  const onDragOverFiles = (e) => { if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setDropActive(true); } };
  const onDropFiles = async (e) => {
    if (!(e.dataTransfer.files && e.dataTransfer.files.length)) {
      if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setDropActive(false); toast.error("Arrastra las fotos desde el Explorador de archivos de Windows, no desde la barra de descargas del navegador."); }
      return;
    }
    e.preventDefault(); setDropActive(false);
    const fd = new FormData(); fd.append("specialty", "");
    [...e.dataTransfer.files].forEach((f) => fd.append("files", f));
    toast.loading("Subiendo a la bandeja general…", { id: "drop" });
    try { await api.post("/photos/bulk", fd); toast.success("Fotos subidas a la bandeja general (Sin especialidad). Clasifícalas arrastrándolas a una carpeta.", { id: "drop" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail), { id: "drop" }); }
  };
  return (
    <div onDragOver={onDragOverFiles} onDragLeave={() => setDropActive(false)} onDrop={onDropFiles} className="relative">
      {dropActive && (
        <div className="fixed inset-0 z-[60] bg-primary/10 border-4 border-dashed border-primary flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
          <div className="bg-white rounded-md px-6 py-4 shadow-xl text-center"><div className="font-heading font-bold text-lg text-primary">Suelta las fotos aquí</div><div className="text-sm text-muted-foreground">Se subirán a la bandeja general para clasificarlas después</div></div>
        </div>
      )}
      <PageHeader overline="Documentación de obra" title="Avance / Fotografías"
        action={<div className="flex gap-2">
          <button onClick={() => setBulkOpen(true)} data-testid="add-photos-bulk-btn" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><Images size={18} weight="bold" /> Subir varias</button>
          <PrimaryButton onClick={() => setOpen(true)} data-testid="add-photo-btn"><Plus size={18} weight="bold" /> Subir foto</PrimaryButton>
        </div>} />

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="photo-filters">
          <span className="text-xs font-semibold text-muted-foreground">Filtrar:</span>
          <select data-testid="filter-specialty" className="h-9 text-sm border border-input rounded-sm px-2 bg-white" value={fSpec} onChange={(e) => setFSpec(e.target.value)}>
            <option value="">Todas las disciplinas</option>
            {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-contract" className="h-9 text-sm border border-input rounded-sm px-2 bg-white" value={fContract} onChange={(e) => setFContract(e.target.value)}>
            <option value="">Todos los proveedores</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          {(fSpec || fContract) && <button onClick={() => { setFSpec(""); setFContract(""); }} className="text-xs text-primary font-semibold hover:underline">Limpiar</button>}
        </div>
      )}

      {items.length > 0 && (
        <div className="mb-6" data-testid="photo-folders-bar">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-muted-foreground">Carpetas (arrastra una foto aquí para clasificarla):</span>
            <button onClick={addFolder} data-testid="add-folder-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><FolderSimplePlus size={14} weight="bold" /> Nueva carpeta</button>
            <button onClick={() => setFoldersLayout((l) => (l === "h" ? "v" : "h"))} data-testid="toggle-folders-layout" title="Cambiar vista de carpetas" className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-semibold border border-input text-muted-foreground hover:bg-muted transition-colors duration-200">
              {foldersLayout === "h" ? <><Rows size={13} weight="bold" /> Vista vertical</> : <><ListDashes size={13} weight="bold" /> Vista horizontal</>}
            </button>
          </div>
          <div className={foldersLayout === "h" ? "flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1" : "flex flex-col items-start gap-2"}>
            {allFolders.map((f) => (
              <div key={f} data-testid={`folder-drop-${f}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (photoDragId) { movePhoto(photoDragId, f); setPhotoDragId(null); toast.success(`Clasificada en "${f}"`); } }}
                className="px-3 py-1.5 rounded-sm border border-dashed border-input text-sm bg-white hover:border-primary hover:bg-primary/5 transition-colors cursor-default flex-shrink-0 whitespace-nowrap">
                {f} <span className="text-xs text-muted-foreground">({items.filter((p) => p.specialty === f).length})</span>
              </div>
            ))}
            <div data-testid="folder-drop-general"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (photoDragId) { movePhoto(photoDragId, ""); setPhotoDragId(null); toast.success("Movida a General"); } }}
              className="px-3 py-1.5 rounded-sm border border-dashed border-input text-sm bg-white hover:border-primary hover:bg-primary/5 transition-colors cursor-default flex-shrink-0 whitespace-nowrap">
              General / Sin especialidad
            </div>
          </div>
        </div>
      )}

      <Card className="p-4 mb-6" data-testid="timemark-photos">
        <div className="font-heading font-bold mb-1 flex items-center gap-2"><Camera size={18} weight="fill" className="text-primary" /> Tomar foto con fecha/hora (se sube al instante)</div>
        <p className="text-xs text-muted-foreground mb-3">La foto se marca con fecha, hora y ubicación y se guarda en la bandeja general para clasificarla después.</p>
        <TimemarkCamera author={user?.name} onCapture={uploadCaptured} />
      </Card>

      {items.length === 0 ? (
        <EmptyState icon={Camera} title="Sin fotografías" subtitle="Documenta el avance físico de la obra subiendo fotos por especialidad (Obra Civil, Inst. Eléctricas, Hidráulicas, Acabados)."
          action={<PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Subir foto</PrimaryButton>} />
      ) : (
        <div className="space-y-8">
          {orderedKeys.map((k) => (
            <div key={k} data-testid={`photo-group-${k}`} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (photoDragId) { movePhoto(photoDragId, k === "General / Sin especialidad" ? "" : k); setPhotoDragId(null); } }}>
              <h3 className="font-heading font-bold text-lg mb-3">{k} <span className="text-sm font-normal text-muted-foreground">({groups[k].length})</span></h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {groups[k].map((p) => (
                  <Card key={p.id} data-testid={`photo-card-${p.id}`} draggable onDragStart={() => setPhotoDragId(p.id)} className="overflow-hidden group cursor-move">
                    <div className="aspect-[4/3] bg-muted overflow-hidden relative cursor-zoom-in" data-testid={`photo-open-${p.id}`} onClick={() => setLightbox({ url: fileUrl(p.storage_path), name: p.caption || "Foto de obra", minimized: false })}>
                      <img src={fileUrl(p.storage_path)} alt={p.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      {canDelete && (
                        <button onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-sm text-danger opacity-0 group-hover:opacity-100 transition-opacity duration-200"><Trash size={16} /></button>
                      )}
                    </div>
                    <div className="p-3">
                      {p.specialty && <span className="inline-block mb-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700">{p.specialty}</span>}
                      <div className="text-sm font-medium truncate">{p.caption || "Sin descripción"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{cmap[p.contract_id]?.title || "General"}</div>
                      <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("es-MX")} · {p.uploaded_by}</div>
                      {p.location && <a href={`https://maps.google.com/?q=${p.location.lat},${p.location.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline mt-1"><MapPin size={12} weight="fill" /> Ubicación</a>}
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
                        <button onClick={() => openEdit(p)} data-testid={`edit-photo-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><PencilSimple size={13} weight="bold" /> Editar</button>
                        <button onClick={() => setAnnotateFor(p)} data-testid={`annotate-photo-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"><PaintBrush size={13} weight="bold" /> Anotar</button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Subir fotografía de avance">
        <form onSubmit={upload} className="space-y-4">
          <Field label="Imagen *">
            <input data-testid="photo-file" type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <Field label="Descripción"><input data-testid="photo-caption" className={inputClass} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ej. Colado de losa nivel 2" /></Field>
          <Field label="Clasificación / carpeta">
            <CategoryField testid="photo-specialty" categories={allFolders} value={specialty} onChange={setSpecialty} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} />
          </Field>
          <Field label="Contrato (opcional)">
            <select className={inputClass} value={contractId} onChange={(e) => setContractId(e.target.value)}>
              <option value="">General</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-photo-btn">{busy ? "Subiendo…" : "Subir"}</PrimaryButton>
          <button type="button" onClick={getLoc} data-testid="photo-location-btn" className={`w-full h-10 rounded-sm font-medium text-sm border transition-colors duration-200 ${coords ? "border-success text-success bg-emerald-50" : "border-input hover:bg-muted"}`}>
            {coords ? `Ubicación adjuntada (${coords.lat}, ${coords.lng})` : "Adjuntar mi ubicación"}
          </button>
        </form>
      </Modal>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Subir varias fotografías">
        <form onSubmit={bulkUpload} className="space-y-4">
          <Field label="Imágenes * (puedes seleccionar varias)">
            <input data-testid="photos-bulk-file" type="file" accept="image/*" multiple onChange={(e) => setBulkFiles(Array.from(e.target.files))}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            {bulkFiles.length > 0 && <p className="text-xs text-muted-foreground mt-1">{bulkFiles.length} imagen(es) seleccionada(s)</p>}
          </Field>
          <Field label="Clasificación / carpeta (opcional — puedes clasificarlas después arrastrándolas)">
            <CategoryField testid="photos-bulk-specialty" categories={allFolders} value={bulkSpecialty} onChange={setBulkSpecialty} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} />
          </Field>
          <Field label="Contrato / proveedor (opcional)">
            <select data-testid="photos-bulk-contract" className={inputClass} value={bulkContract} onChange={(e) => setBulkContract(e.target.value)}>
              <option value="">General</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <PrimaryButton type="submit" disabled={bulkBusy} data-testid="save-photos-bulk-btn">{bulkBusy ? "Subiendo…" : `Subir ${bulkFiles.length || ""} foto(s)`}</PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!editFor} onClose={() => setEditFor(null)} title="Editar fotografía">
        <form onSubmit={saveEdit} className="space-y-4">
          <Field label="Título / descripción"><input data-testid="edit-photo-caption" className={inputClass} value={editForm.caption} onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })} placeholder="Ej. Colado de firme de tanque pluvial" /></Field>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setEditForm({ ...editForm, caption: editForm.caption.toUpperCase() })} className="text-xs px-2 py-1 border border-input rounded-sm hover:bg-muted">MAYÚSCULAS</button>
            <button type="button" onClick={() => setEditForm({ ...editForm, caption: editForm.caption.toLowerCase() })} className="text-xs px-2 py-1 border border-input rounded-sm hover:bg-muted">minúsculas</button>
            <button type="button" onClick={() => setEditForm({ ...editForm, caption: editForm.caption.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()) })} className="text-xs px-2 py-1 border border-input rounded-sm hover:bg-muted">Formato Título</button>
          </div>
          <Field label="Clasificación / carpeta">
            <CategoryField testid="edit-photo-specialty" categories={allFolders} value={editForm.specialty} onChange={(v) => setEditForm({ ...editForm, specialty: v })} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} />
          </Field>
          <Field label="Contrato">
            <select data-testid="edit-photo-contract" className={inputClass} value={editForm.contract_id} onChange={(e) => setEditForm({ ...editForm, contract_id: e.target.value })}>
              <option value="">General</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <PrimaryButton type="submit" data-testid="save-edit-photo-btn">Guardar cambios</PrimaryButton>
        </form>
      </Modal>

      <AnnotationEditor open={!!annotateFor} onClose={() => setAnnotateFor(null)} storagePath={annotateFor?.storage_path} onSave={saveAnnotation} saving={annBusy} />
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} onMinimize={(m) => setLightbox((v) => v && { ...v, minimized: m })} />
    </div>
  );
}
